// MarketingHeroDemo.jsx
//
// Auto-playing animated demo that replaces the static HeroPreview on the
// marketing home page. Six scenes, ~4 seconds each, looping:
//
//   1. Client intake form (fields type in, submit pulses, success)
//   2. Conflict check (radar sweep, "no conflicts found")
//   3. Case file opens (card slides in with status + deadlines)
//   4. Time tracking (stopwatch ticks, entry lands in the list)
//   5. Invoice generation (line items appear, total counts up to GH₵ 4,723.50)
//   6. Payment received (Paystack-style success)
//
// Implementation notes:
//   * Pure CSS keyframe animations + React state for scene-level
//     orchestration. No external animation library — zero bundle cost.
//   * Auto-advance via setInterval. Paused while the user hovers
//     (so they can actually read it) or when they manually pause.
//   * Crossfade between scenes via CSS opacity transitions.
//   * Each scene is keyed by its index — remounting on scene change
//     replays its entry animations from frame zero (no manual reset).
//   * Manual next/prev buttons + clickable progress dots.
//
// To screen-record as a video: pause auto-play, screen-record at 60fps
// for ~28 seconds (one full loop + 4 sec buffer), and you've got a
// promo MP4. Or leave it embedded — that's the modern approach.

import React, { useEffect, useState, useRef } from 'react';
import {
  Briefcase, Users, Clock, DollarSign, FileText, MessageSquare,
  Building2, Calendar, ArrowRight, Check, Search, Scale, Receipt,
  Sparkles, Play, Pause, ChevronLeft, ChevronRight, ShieldCheck,
  Mail, Phone, Send,
} from 'lucide-react';
import { BRAND } from './marketingTokens';

// ─── Scene metadata ────────────────────────────────────────────────
const SCENES = [
  { id: 'intake',   title: 'Client intakes itself',        sub: 'Your intake form captures the basics — and creates the contact for you.' },
  { id: 'conflict', title: 'Conflict check, instantly',    sub: 'Every new file is screened against every existing client. No more "wait, do we already act for them?".' },
  { id: 'casefile', title: 'One case file, every party',   sub: 'Status, deadlines, team — all in one place. Confidential files stay confidential.' },
  { id: 'time',     title: 'Track time as you work',       sub: 'One-click entries from any case file. No more end-of-month "what did I do?" guessing.' },
  { id: 'invoice',  title: 'Bill in cedis, get paid',      sub: 'VAT + NHIL lines built in. Pay link goes to your client via Paystack.' },
  { id: 'paid',     title: 'Payment lands in your account', sub: 'Paystack confirms in seconds. We auto-record it against the matter and update the trust ledger.' },
];

const SCENE_MS = 4500;  // each scene's duration

// ─── CSS — all keyframes the scenes need ────────────────────────────
const STYLES = `
@keyframes mhd-fadein     { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes mhd-slidein-l  { from { opacity: 0; transform: translateX(-24px); } to { opacity: 1; transform: translateX(0); } }
@keyframes mhd-slidein-r  { from { opacity: 0; transform: translateX(24px); }  to { opacity: 1; transform: translateX(0); } }
@keyframes mhd-pulse-cta  { 0%, 100% { box-shadow: 0 4px 16px rgba(249,115,22,0.30); } 50% { box-shadow: 0 8px 28px rgba(249,115,22,0.55); } }
@keyframes mhd-radar-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes mhd-radar-ping { 0% { transform: scale(0.6); opacity: 0.85; } 100% { transform: scale(1.6); opacity: 0; } }
@keyframes mhd-checkmark  { 0% { stroke-dashoffset: 24; } 100% { stroke-dashoffset: 0; } }
@keyframes mhd-blink      { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
@keyframes mhd-tick       { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes mhd-bar-grow   { from { width: 0; } to { width: var(--mhd-target, 100%); } }
@keyframes mhd-success    { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.15); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
@keyframes mhd-shimmer    { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
@keyframes mhd-coin-fly   { 0% { transform: translate(-50%, -50%) scale(0.2); opacity: 0; } 30% { opacity: 1; } 100% { transform: translate(-50%, -160px) scale(1); opacity: 0; } }

.mhd-fade-in   { animation: mhd-fadein 0.55s cubic-bezier(.22,1,.36,1) both; }
.mhd-slide-l   { animation: mhd-slidein-l 0.55s cubic-bezier(.22,1,.36,1) both; }
.mhd-slide-r   { animation: mhd-slidein-r 0.55s cubic-bezier(.22,1,.36,1) both; }
.mhd-blink     { animation: mhd-blink 1s linear infinite; }
.mhd-pulse-cta { animation: mhd-pulse-cta 1.6s ease-in-out infinite; }
.mhd-tick      { animation: mhd-tick 2.5s linear infinite; }
.mhd-success   { animation: mhd-success 0.45s cubic-bezier(.22,1.5,.36,1) both; }
.mhd-coin      { animation: mhd-coin-fly 1.4s ease-out forwards; }

/* Crossfade for the scene container */
.mhd-scene-wrap { position: relative; }
.mhd-scene      { position: absolute; inset: 0; transition: opacity 0.35s ease, transform 0.35s ease; }
.mhd-scene[data-active="false"] { opacity: 0; transform: scale(0.985); pointer-events: none; }
.mhd-scene[data-active="true"]  { opacity: 1; transform: scale(1); pointer-events: auto; }
`;

// ─── Component ─────────────────────────────────────────────────────

export default function MarketingHeroDemo({ onSceneChange }) {
  const [scene, setScene]     = useState(0);
  const [playing, setPlaying] = useState(true);
  const [hovering, setHover]  = useState(false);
  const timerRef = useRef(null);

  // Fire onSceneChange whenever the scene index changes, so a parent
  // (e.g. WatchDemoModal) can record analytics. Wrapped in useEffect
  // so the callback fires AFTER React commits — not during render.
  useEffect(() => {
    if (onSceneChange) onSceneChange(scene);
  }, [scene, onSceneChange]);

  // Auto-advance. Pauses while playing=false OR the user is hovering
  // (so they can actually read the scene without it flicking away).
  useEffect(() => {
    if (!playing || hovering) return undefined;
    timerRef.current = setTimeout(() => {
      setScene((s) => (s + 1) % SCENES.length);
    }, SCENE_MS);
    return () => clearTimeout(timerRef.current);
  }, [scene, playing, hovering]);

  const next  = () => setScene((s) => (s + 1) % SCENES.length);
  const prev  = () => setScene((s) => (s - 1 + SCENES.length) % SCENES.length);
  const goTo  = (i) => setScene(i);
  const toggle = () => setPlaying((p) => !p);

  const meta = SCENES[scene];

  return (
    <div className="mt-16 lg:mt-20 max-w-5xl mx-auto">
      <style>{STYLES}</style>

      <div
        className="rounded-2xl overflow-hidden border relative"
        style={{
          background: BRAND.bgWhite,
          borderColor: BRAND.border,
          boxShadow: '0 24px 64px rgba(15,42,95,0.18), 0 8px 24px rgba(15,42,95,0.08)',
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {/* Browser chrome */}
        <div
          className="flex items-center gap-2 px-4 py-3 border-b"
          style={{ background: BRAND.bgLight, borderColor: BRAND.border }}
        >
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#FF5F57' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#FEBC2E' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28C840' }} />
          <div
            className="ml-4 px-3 py-1 rounded-md text-xs flex-1 max-w-[280px] truncate"
            style={{ background: BRAND.bgWhite, color: BRAND.textFaint, border: `1px solid ${BRAND.border}` }}
          >
            app.docketflowapp.com
          </div>
          <div
            className="hidden sm:flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold"
            style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
          >
            <Sparkles size={10} />
            Live demo
          </div>
        </div>

        {/* Stage — fixed-height crossfade container so the layout
            doesn't jump as scenes change. */}
        <div
          className="mhd-scene-wrap"
          style={{ minHeight: 420 }}
        >
          <div className="mhd-scene" data-active={scene === 0} aria-hidden={scene !== 0}>
            <SceneIntake active={scene === 0} />
          </div>
          <div className="mhd-scene" data-active={scene === 1} aria-hidden={scene !== 1}>
            <SceneConflict active={scene === 1} />
          </div>
          <div className="mhd-scene" data-active={scene === 2} aria-hidden={scene !== 2}>
            <SceneCaseFile active={scene === 2} />
          </div>
          <div className="mhd-scene" data-active={scene === 3} aria-hidden={scene !== 3}>
            <SceneTime active={scene === 3} />
          </div>
          <div className="mhd-scene" data-active={scene === 4} aria-hidden={scene !== 4}>
            <SceneInvoice active={scene === 4} />
          </div>
          <div className="mhd-scene" data-active={scene === 5} aria-hidden={scene !== 5}>
            <ScenePaid active={scene === 5} />
          </div>
        </div>

        {/* Controls strip */}
        <div
          className="px-5 py-4 border-t flex items-center gap-3"
          style={{ background: BRAND.bgLight, borderColor: BRAND.border }}
        >
          {/* Title + sub for the current scene */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold leading-tight" style={{ color: BRAND.textHead }}>
              {meta.title}
            </p>
            <p className="text-xs leading-snug truncate" style={{ color: BRAND.textMuted }}>
              {meta.sub}
            </p>
          </div>

          {/* Prev / play-pause / next */}
          <div className="flex items-center gap-1">
            <ControlBtn onClick={prev} ariaLabel="Previous scene">
              <ChevronLeft size={14} />
            </ControlBtn>
            <ControlBtn onClick={toggle} ariaLabel={playing ? 'Pause' : 'Play'} primary>
              {playing ? <Pause size={13} /> : <Play size={13} />}
            </ControlBtn>
            <ControlBtn onClick={next} ariaLabel="Next scene">
              <ChevronRight size={14} />
            </ControlBtn>
          </div>
        </div>

        {/* Progress dots */}
        <div
          className="flex items-center justify-center gap-1.5 py-3 border-t"
          style={{ borderColor: BRAND.border, background: BRAND.bgWhite }}
        >
          {SCENES.map((s, i) => (
            <button
              key={s.id}
              onClick={() => goTo(i)}
              aria-label={`Go to scene ${i + 1}: ${s.title}`}
              className="transition-all"
              style={{
                width:  i === scene ? 24 : 6,
                height: 6,
                borderRadius: 999,
                background: i === scene ? BRAND.orange : BRAND.border,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Control button ────────────────────────────────────────────────
function ControlBtn({ children, onClick, ariaLabel, primary }) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className="w-7 h-7 rounded-md flex items-center justify-center transition-all"
      style={{
        background: primary ? BRAND.blueDeep : BRAND.bgWhite,
        color:      primary ? '#fff'          : BRAND.textMuted,
        border:    `1px solid ${primary ? BRAND.blueDeep : BRAND.border}`,
      }}
      onMouseEnter={(e) => { if (!primary) e.currentTarget.style.background = BRAND.bgLighter; }}
      onMouseLeave={(e) => { if (!primary) e.currentTarget.style.background = BRAND.bgWhite; }}
    >
      {children}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────
// SCENES
// ────────────────────────────────────────────────────────────────────

// ── Reusable shell so every scene has the same sidebar + page header
// (creates a sense of "this is the same app, different page").
function SceneShell({ active, activeNav, pageTitle, pageSubtitle, children }) {
  const navItems = [
    { icon: Building2,    label: 'Dashboard',  key: 'dashboard' },
    { icon: Briefcase,    label: 'Case Files', key: 'casefiles' },
    { icon: Users,        label: 'Contacts',   key: 'contacts' },
    { icon: Calendar,     label: 'Calendar',   key: 'calendar' },
    { icon: Clock,        label: 'Time',       key: 'time' },
    { icon: DollarSign,   label: 'Billing',    key: 'billing' },
    { icon: FileText,     label: 'Documents',  key: 'documents' },
    { icon: MessageSquare,label: 'Messages',   key: 'messages' },
  ];

  return (
    <div className="grid grid-cols-[180px_1fr] min-h-[420px]">
      {/* Sidebar */}
      <div
        className="p-3 border-r space-y-1"
        style={{ background: BRAND.blueDeep, borderColor: BRAND.border }}
      >
        <div className="flex items-center gap-2 px-2 py-2 mb-3">
          <img src="/logo-transparent.png" alt="" className="w-6 h-6" />
          <span className="text-sm font-bold text-white">DocketFlow</span>
        </div>
        {navItems.map((it) => {
          const Ic = it.icon;
          const isActive = it.key === activeNav;
          return (
            <div
              key={it.label}
              className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs ${active && isActive ? 'mhd-slide-l' : ''}`}
              style={{
                background: isActive ? 'rgba(249,115,22,0.18)' : 'transparent',
                color:      isActive ? '#FFE6CC' : '#94A3B8',
                transition: 'background 0.3s ease, color 0.3s ease',
              }}
            >
              <Ic size={13} />
              {it.label}
            </div>
          );
        })}
      </div>

      {/* Main canvas */}
      <div className="p-6">
        <div className="mb-5">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: BRAND.textFaint }}>
            {pageSubtitle}
          </p>
          <h3 className="text-lg font-bold mt-0.5" style={{ color: BRAND.textHead }}>
            {pageTitle}
          </h3>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Typing effect hook — used for the intake form fields ──────────
function useTyped(text, active, startDelayMs = 0) {
  const [shown, setShown] = useState('');
  useEffect(() => {
    if (!active) { setShown(''); return undefined; }
    let timeouts = [];
    setShown('');
    const start = setTimeout(() => {
      [...text].forEach((char, i) => {
        const t = setTimeout(() => setShown(text.slice(0, i + 1)), i * 35);
        timeouts.push(t);
      });
    }, startDelayMs);
    timeouts.push(start);
    return () => timeouts.forEach(clearTimeout);
  }, [text, active, startDelayMs]);
  return shown;
}

// ── Scene 1: Client intake ─────────────────────────────────────────
function SceneIntake({ active }) {
  const firstName = useTyped('Akua',          active, 200);
  const lastName  = useTyped('Boateng',        active, 700);
  const email     = useTyped('akua@example.com', active, 1300);
  const phone     = useTyped('+233 24 555 0142', active, 2000);
  // Show "submitted" state at ~3.4s
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    if (!active) { setSubmitted(false); return undefined; }
    const t = setTimeout(() => setSubmitted(true), 3300);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <SceneShell active={active} activeNav="contacts" pageSubtitle="Intake" pageTitle="New client intake">
      <div className="max-w-md">
        {submitted ? (
          <div className="mhd-fade-in flex flex-col items-center text-center py-12">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4 mhd-success"
              style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
            >
              <Check size={28} strokeWidth={3} />
            </div>
            <p className="text-base font-bold" style={{ color: BRAND.textHead }}>
              Akua Boateng — contact created
            </p>
            <p className="text-xs mt-1" style={{ color: BRAND.textMuted }}>
              Adding to your client roster…
            </p>
          </div>
        ) : (
          <div className="space-y-3 mhd-fade-in">
            <FakeField icon={Users} label="First name" value={firstName} cursor />
            <FakeField icon={Users} label="Last name"  value={lastName}  cursor={firstName.length === 4} />
            <FakeField icon={Mail}  label="Email"      value={email}     cursor={lastName === 'Boateng'} />
            <FakeField icon={Phone} label="Phone"      value={phone}     cursor={email.length >= 16} />

            <button
              className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-semibold rounded-lg ${phone.length >= 16 ? 'mhd-pulse-cta' : ''}`}
              style={{
                background: phone.length >= 16
                  ? `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`
                  : BRAND.bgLighter,
                color: phone.length >= 16 ? '#fff' : BRAND.textFaint,
              }}
              type="button"
            >
              Save contact
              <ArrowRight size={12} />
            </button>
          </div>
        )}
      </div>
    </SceneShell>
  );
}

function FakeField({ icon: Ic, label, value, cursor }) {
  return (
    <div>
      <p className="text-[10px] font-semibold mb-1" style={{ color: BRAND.textMuted }}>
        {label}
      </p>
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-lg border"
        style={{ borderColor: BRAND.border, background: BRAND.bgWhite }}
      >
        <Ic size={12} style={{ color: BRAND.textFaint }} />
        <span className="text-xs flex-1" style={{ color: BRAND.textBody }}>
          {value}
          {cursor && <span className="mhd-blink ml-0.5" style={{ color: BRAND.blueUi }}>|</span>}
        </span>
      </div>
    </div>
  );
}

// ── Scene 2: Conflict check ─────────────────────────────────────────
function SceneConflict({ active }) {
  // Sweep then resolve to "no conflicts" at ~2.5s
  const [resolved, setResolved] = useState(false);
  useEffect(() => {
    if (!active) { setResolved(false); return undefined; }
    const t = setTimeout(() => setResolved(true), 2400);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <SceneShell active={active} activeNav="casefiles" pageSubtitle="Conflict check" pageTitle="Akua Boateng — new file">
      <div className="flex flex-col items-center justify-center py-6">
        {/* Radar */}
        <div className="relative w-44 h-44 mb-6">
          {/* Concentric rings */}
          {[0.4, 0.65, 0.9].map((scale, i) => (
            <div
              key={i}
              className="absolute inset-0 rounded-full"
              style={{
                border: `2px solid ${resolved ? BRAND.orangeSoft : BRAND.blueGhost}`,
                transform: `scale(${scale})`,
                transition: 'border-color 0.5s ease',
              }}
            />
          ))}
          {/* Spinning radar arm */}
          {!resolved && (
            <div
              className="absolute top-1/2 left-1/2 w-1/2 h-1 origin-left"
              style={{
                background: `linear-gradient(90deg, ${BRAND.blueUi} 0%, transparent 100%)`,
                transform: 'translate(0, -50%)',
                animation: 'mhd-radar-spin 1.4s linear infinite',
              }}
            />
          )}
          {/* Center icon */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full flex items-center justify-center"
            style={{
              background: resolved ? BRAND.orangeSoft : BRAND.blueDeep,
              color:      resolved ? BRAND.orangeDark : '#fff',
              transition: 'background 0.5s ease, color 0.5s ease',
              boxShadow: resolved ? '0 8px 24px rgba(249,115,22,0.32)' : '0 8px 24px rgba(15,42,95,0.32)',
            }}
          >
            {resolved ? <Check size={26} strokeWidth={3} className="mhd-success" /> : <Search size={22} />}
          </div>
          {/* Pinging dot for one of the "checked" contacts */}
          {!resolved && (
            <div
              className="absolute top-[30%] right-[18%] w-3 h-3 rounded-full"
              style={{
                background: BRAND.blueSoft,
                animation: 'mhd-radar-ping 1.2s ease-out infinite',
              }}
            />
          )}
        </div>

        <p
          className="text-base font-bold mb-1"
          style={{ color: resolved ? BRAND.orangeDark : BRAND.blueDeep }}
        >
          {resolved ? 'No conflicts found' : 'Checking 184 existing clients…'}
        </p>
        <p className="text-xs" style={{ color: BRAND.textMuted }}>
          {resolved ? 'Cleared against parties, related cases, and adverse representations.' : 'Cross-referencing parties and matter history…'}
        </p>

        {resolved && (
          <button
            className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg mhd-fade-in"
            style={{
              background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
              color: '#fff',
            }}
            type="button"
          >
            Open case file
            <ArrowRight size={11} />
          </button>
        )}
      </div>
    </SceneShell>
  );
}

// ── Scene 3: Case file open ─────────────────────────────────────────
function SceneCaseFile({ active }) {
  return (
    <SceneShell active={active} activeNav="casefiles" pageSubtitle="Case file" pageTitle="Boateng Estate planning">
      <div className="space-y-4">
        {/* Header card */}
        <div
          className="rounded-xl border p-4 mhd-fade-in"
          style={{ borderColor: BRAND.border, background: BRAND.bgWhite }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ background: BRAND.blueGhost, color: BRAND.blueDeep }}
              >
                <Briefcase size={16} />
              </div>
              <div>
                <p className="text-xs" style={{ color: BRAND.textMuted }}>
                  Client: Akua Boateng · Probate
                </p>
                <p className="text-sm font-bold" style={{ color: BRAND.textHead }}>
                  Estate planning — wills and trust
                </p>
              </div>
            </div>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider"
              style={{ background: BRAND.blueGhost, color: BRAND.blueDeep }}
            >
              Open
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Responsible',  value: 'Ama O.',  i: 0 },
              { label: 'Opened',       value: '20 May',  i: 1 },
              { label: 'Next deadline', value: '28 May', i: 2 },
            ].map((m) => (
              <div
                key={m.label}
                className="rounded-md p-2 mhd-fade-in"
                style={{
                  background: BRAND.bgLight,
                  animationDelay: `${100 + m.i * 90}ms`,
                }}
              >
                <p className="text-[9px] uppercase tracking-wide font-bold" style={{ color: BRAND.textFaint }}>
                  {m.label}
                </p>
                <p className="text-xs font-semibold mt-0.5" style={{ color: BRAND.textHead }}>
                  {m.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Team + deadlines */}
        <div className="grid grid-cols-2 gap-3">
          <div
            className="rounded-xl border p-3 mhd-slide-l"
            style={{ borderColor: BRAND.border, background: BRAND.bgWhite, animationDelay: '350ms' }}
          >
            <p className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: BRAND.textFaint }}>
              Team
            </p>
            {[
              { name: 'Ama Owusu',     role: 'Partner' },
              { name: 'Kwame Adjei',   role: 'Associate' },
              { name: 'Pamela Asante', role: 'Paralegal' },
            ].map((t, i) => (
              <div key={t.name} className="flex items-center gap-2 py-1">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold"
                  style={{
                    background: i === 0 ? BRAND.orange : (i === 1 ? BRAND.blueUi : BRAND.blueSoft),
                    color: '#fff',
                  }}
                >
                  {t.name.split(' ').map((p) => p[0]).join('')}
                </div>
                <div className="flex-1">
                  <p className="text-[11px] font-semibold" style={{ color: BRAND.textHead }}>{t.name}</p>
                  <p className="text-[9px]" style={{ color: BRAND.textMuted }}>{t.role}</p>
                </div>
              </div>
            ))}
          </div>

          <div
            className="rounded-xl border p-3 mhd-slide-r"
            style={{ borderColor: BRAND.border, background: BRAND.bgWhite, animationDelay: '400ms' }}
          >
            <p className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: BRAND.textFaint }}>
              Upcoming deadlines
            </p>
            {[
              { label: 'Draft will',         when: '28 May' },
              { label: 'Beneficiary meeting', when: '3 Jun' },
              { label: 'File at Registrar',   when: '15 Jun' },
            ].map((d) => (
              <div key={d.label} className="flex items-center gap-2 py-1">
                <div className="w-1.5 h-5 rounded-full" style={{ background: BRAND.orange }} />
                <p className="text-[11px] flex-1 truncate" style={{ color: BRAND.textBody }}>{d.label}</p>
                <p className="text-[9px] font-semibold" style={{ color: BRAND.blueDeep }}>{d.when}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SceneShell>
  );
}

// ── Scene 4: Time tracking ──────────────────────────────────────────
function SceneTime({ active }) {
  // After ~2s, the new entry gets "saved" and a new row appears.
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!active) { setSaved(false); return undefined; }
    const t = setTimeout(() => setSaved(true), 2200);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <SceneShell active={active} activeNav="time" pageSubtitle="Time" pageTitle="Your time today">
      <div className="space-y-3">
        {/* Active timer card */}
        <div
          className="rounded-xl border p-3 mhd-fade-in"
          style={{ borderColor: BRAND.border, background: BRAND.bgWhite }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center mhd-tick"
              style={{
                background: `conic-gradient(${BRAND.orange} 270deg, ${BRAND.bgLight} 0)`,
                color: BRAND.orangeDark,
              }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: BRAND.bgWhite, color: BRAND.orangeDark }}
              >
                <Clock size={13} />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-xs" style={{ color: BRAND.textMuted }}>
                Boateng Estate planning · drafting will
              </p>
              <p className="text-base font-bold tabular-nums" style={{ color: BRAND.textHead }}>
                01:24:08
              </p>
            </div>
            <button
              className="px-3 py-1.5 text-xs font-semibold rounded-lg"
              style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
              type="button"
            >
              Stop
            </button>
          </div>
        </div>

        {/* List of today's entries */}
        <div className="space-y-1.5">
          <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: BRAND.textFaint }}>
            Today
          </p>
          {/* New entry that appears after timer "stops" */}
          {saved && (
            <div
              className="rounded-lg border p-2.5 mhd-slide-r flex items-center gap-3"
              style={{ borderColor: BRAND.orangeSoft, background: 'rgba(249,115,22,0.04)' }}
            >
              <div
                className="w-7 h-7 rounded-md flex items-center justify-center"
                style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
              >
                <Clock size={12} />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold" style={{ color: BRAND.textHead }}>
                  Boateng — drafting will
                </p>
                <p className="text-[10px]" style={{ color: BRAND.textMuted }}>
                  Just now · billable
                </p>
              </div>
              <p className="text-sm font-bold tabular-nums" style={{ color: BRAND.orangeDark }}>
                1.4h
              </p>
            </div>
          )}

          {[
            { matter: 'Mensah v. Republic', task: 'court appearance',  hours: '2.0h', when: '10:30 AM' },
            { matter: 'Asante Trading Ltd', task: 'reply to GRA',      hours: '1.5h', when: '9:00 AM' },
          ].map((e, i) => (
            <div
              key={e.matter}
              className="rounded-lg border p-2.5 flex items-center gap-3 mhd-fade-in"
              style={{
                borderColor: BRAND.border,
                background: BRAND.bgWhite,
                animationDelay: `${200 + i * 100}ms`,
              }}
            >
              <div
                className="w-7 h-7 rounded-md flex items-center justify-center"
                style={{ background: BRAND.blueGhost, color: BRAND.blueDeep }}
              >
                <Clock size={12} />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold" style={{ color: BRAND.textHead }}>
                  {e.matter}
                </p>
                <p className="text-[10px]" style={{ color: BRAND.textMuted }}>
                  {e.task} · {e.when}
                </p>
              </div>
              <p className="text-sm font-bold tabular-nums" style={{ color: BRAND.blueDeep }}>
                {e.hours}
              </p>
            </div>
          ))}
        </div>
      </div>
    </SceneShell>
  );
}

// ── Scene 5: Invoice ────────────────────────────────────────────────
function SceneInvoice({ active }) {
  // Line items appear one by one; the total counts up.
  // The "Send invoice" button arrives once the total settles.
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!active) { setStep(0); return undefined; }
    const timers = [
      setTimeout(() => setStep(1), 350),
      setTimeout(() => setStep(2), 800),
      setTimeout(() => setStep(3), 1250),
      setTimeout(() => setStep(4), 1700),
      setTimeout(() => setStep(5), 2350),  // total settles
      setTimeout(() => setStep(6), 3000),  // send button enters
    ];
    return () => timers.forEach(clearTimeout);
  }, [active]);

  const lines = [
    { d: 'Drafting will — A. Boateng',          h: '4.5', a: 2250 },
    { d: 'Client conference + memo',            h: '2.0', a: 1000 },
    { d: 'Filing fee — Estate Registrar',       h: '—',   a:  320 },
    { d: 'Junior research (paralegal)',         h: '3.0', a:  450 },
  ];

  // Approx running subtotal during the line-by-line reveal — values are
  // tuned to feel right as each line lands, not to sum mathematically
  // perfectly (the final total settles at the real GH₵ 4,020 by step 4).
  const runningSub = step === 1 ? 2250 : step === 2 ? 3250 : step === 3 ? 3570 : step >= 4 ? 4020 : 0;
  const vat = step >= 5 ? 603    : 0;
  const nhil = step >= 5 ? 100.5 : 0;
  const total = step >= 5 ? 4723.5 : runningSub;

  return (
    <SceneShell active={active} activeNav="billing" pageSubtitle="Invoice" pageTitle="INV-0042 — Akua Boateng">
      <div className="rounded-xl border overflow-hidden mhd-fade-in" style={{ borderColor: BRAND.border, background: BRAND.bgWhite }}>
        {/* Header */}
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: BRAND.bgLight, borderBottom: `1px solid ${BRAND.border}` }}>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: BRAND.textFaint }}>Bill to</p>
            <p className="text-xs font-bold mt-0.5" style={{ color: BRAND.textHead }}>Akua Boateng</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-semibold" style={{ color: BRAND.textMuted }}>Due 31 May 2026</p>
            <p className="text-[10px]" style={{ color: BRAND.textFaint }}>Issued today</p>
          </div>
        </div>

        {/* Line items */}
        <div>
          <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-1.5 text-[9px] uppercase tracking-wider font-bold"
               style={{ background: BRAND.bgLighter, color: BRAND.textFaint }}>
            <span>Description</span>
            <span className="text-right">Hours</span>
            <span className="text-right">Amount</span>
          </div>
          {lines.map((line, i) => (
            <div
              key={i}
              className={`grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-1.5 text-[10px] ${step > i ? 'mhd-slide-r' : ''}`}
              style={{
                borderTop: `1px solid ${BRAND.borderSoft}`,
                background: BRAND.bgWhite,
                color: BRAND.textBody,
                opacity: step > i ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
            >
              <span className="truncate">{line.d}</span>
              <span className="text-right tabular-nums" style={{ color: BRAND.textMuted }}>{line.h}</span>
              <span className="text-right tabular-nums font-semibold">
                GH₵ {line.a.toLocaleString('en-US', { minimumFractionDigits: 0 })}
              </span>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="px-4 py-3 space-y-1 border-t" style={{ borderColor: BRAND.border, background: BRAND.bgLight }}>
          {[
            { l: 'Subtotal', v: runningSub, strong: false, show: step >= 1 },
            { l: 'VAT (15%)',   v: vat,     strong: false, show: step >= 5 },
            { l: 'NHIL (2.5%)', v: nhil,    strong: false, show: step >= 5 },
            { l: 'Total',       v: total,   strong: true,  show: true },
          ].filter((r) => r.show).map((row, i) => (
            <div
              key={row.l + i}
              className={`flex items-center justify-between text-[10.5px] ${row.strong ? 'pt-1.5 mt-1.5' : ''}`}
              style={{
                color: row.strong ? BRAND.textHead : BRAND.textMuted,
                fontWeight: row.strong ? 700 : 500,
                borderTop: row.strong ? `1px solid ${BRAND.border}` : 'none',
                transition: 'color 0.3s ease',
              }}
            >
              <span>{row.l}</span>
              <span className="tabular-nums">
                GH₵ {row.v.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
          ))}
        </div>

        {/* Send invoice button */}
        {step >= 6 && (
          <div className="px-4 pb-4 pt-2 mhd-fade-in">
            <button
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-semibold rounded-lg mhd-pulse-cta"
              style={{
                background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
                color: '#fff',
              }}
              type="button"
            >
              <Send size={11} />
              Send invoice to Akua
            </button>
          </div>
        )}
      </div>
    </SceneShell>
  );
}

// ── Scene 6: Payment received ───────────────────────────────────────
function ScenePaid({ active }) {
  // Big success check + Paystack confirmation.
  return (
    <SceneShell active={active} activeNav="billing" pageSubtitle="Payment received" pageTitle="INV-0042 paid in full">
      <div className="relative flex flex-col items-center text-center py-8">
        {/* Floating coin animations */}
        {active && [0, 0.25, 0.5, 0.75].map((delay, i) => (
          <div
            key={i}
            className="absolute top-1/2 left-1/2 mhd-coin"
            style={{
              animationDelay: `${delay}s`,
              transform: `translate(${-50 + (i - 1.5) * 30}%, -50%)`,
            }}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
              style={{
                background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
                color: '#fff',
                boxShadow: '0 4px 12px rgba(249,115,22,0.35)',
              }}
            >
              ₵
            </div>
          </div>
        ))}

        {/* Success check */}
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-5 mhd-success relative z-10"
          style={{
            background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
            color: '#fff',
            boxShadow: '0 12px 32px rgba(249,115,22,0.40)',
          }}
        >
          <Check size={38} strokeWidth={3} />
        </div>

        <p className="text-xl font-bold mb-1" style={{ color: BRAND.textHead }}>
          GH₵ 4,723.50 received
        </p>
        <p className="text-xs mb-5" style={{ color: BRAND.textMuted }}>
          Paid by Akua Boateng · via Paystack
        </p>

        {/* Confirmation pills */}
        <div className="flex flex-wrap gap-2 justify-center">
          {[
            { Ic: Receipt,     label: 'INV-0042 marked paid' },
            { Ic: Scale,       label: 'Trust ledger updated' },
            { Ic: ShieldCheck, label: 'Audit log written' },
          ].map((pill, i) => {
            const Ic = pill.Ic;
            return (
              <div
                key={pill.label}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold mhd-fade-in"
                style={{
                  background: BRAND.blueGhost,
                  color: BRAND.blueDeep,
                  animationDelay: `${500 + i * 200}ms`,
                }}
              >
                <Ic size={11} />
                {pill.label}
              </div>
            );
          })}
        </div>
      </div>
    </SceneShell>
  );
}
